// Navigation preferences: the order and visibility of the top-nav items.
//
// Account-scoped when a PreferencesProvider is mounted (so they follow the user
// to another browser), falling back to the per-browser localStorage key
// otherwise. Only the fallback path writes that key: under the provider it is
// left frozen as the one-time migration source, and PreferencesContext keeps
// its own consolidated paint cache.
//
// The Navbar owns the single hook instance and passes the derived items +
// mutators down to NavSettingsDialog, so a change made in the dialog reflects
// in the nav immediately (both mount at the same time). The defensive
// load/merge/persist shape mirrors useHabitStatsPreferences and the other
// kalpa- preference stores.
import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePreferences } from '../contexts/PreferencesContext';

export const NAV_PREFERENCES_STORAGE_KEY = 'kalpa-nav-preferences';

export interface NavItem {
  label: string;
  path: string;
}

// A nav item enriched with its current visibility, in persisted order.
export interface OrderedNavItem extends NavItem {
  hidden: boolean;
}

interface NavPreferences {
  // Ordered list of item paths (path is the stable identifier — items have no id).
  order: string[];
  // Paths the user has hidden from the nav.
  hidden: string[];
}

const defaultPreferences = (defaultPaths: string[]): NavPreferences => ({
  order: [...defaultPaths],
  hidden: [],
});

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((entry) => typeof entry === 'string');

// Merge stored prefs over the current default paths so the nav stays correct as
// items are added/removed in code: unknown/stale paths are dropped, and any new
// default path is appended (visible) at the end. Never throws — corrupt or
// inaccessible storage falls back to the defaults.
export const loadNavPreferences = (
  defaultPaths: string[],
  stored?: Partial<NavPreferences> | null,
): NavPreferences => {
  try {
    let parsed: Partial<NavPreferences>;
    if (stored !== undefined) {
      // Value supplied by the server-backed store.
      if (!stored || Object.keys(stored).length === 0) {
        return defaultPreferences(defaultPaths);
      }
      parsed = stored;
    } else {
      const raw = localStorage.getItem(NAV_PREFERENCES_STORAGE_KEY);
      if (!raw) return defaultPreferences(defaultPaths);
      parsed = JSON.parse(raw) as Partial<NavPreferences>;
    }
    const known = new Set(defaultPaths);

    const storedOrder = isStringArray(parsed.order) ? parsed.order : [];
    const order = storedOrder.filter((path) => known.has(path));
    defaultPaths.forEach((path) => {
      if (!order.includes(path)) order.push(path);
    });

    const storedHidden = isStringArray(parsed.hidden) ? parsed.hidden : [];
    // A brand-new item is never in stored hidden, so it defaults visible.
    const hidden = storedHidden.filter((path) => known.has(path));

    return { order, hidden };
  } catch {
    return defaultPreferences(defaultPaths);
  }
};

export const useNavPreferences = (defaultItems: NavItem[]) => {
  const store = usePreferences();
  const defaultPaths = useMemo(
    () => defaultItems.map((item) => item.path),
    [defaultItems]
  );

  const [localPreferences, setLocalPreferences] = useState<NavPreferences>(() =>
    loadNavPreferences(defaultPaths)
  );

  // The server value is merged over the current default paths on every read, so
  // items added or removed in code stay correct without a write.
  const preferences = useMemo(() => {
    if (!store) return localPreferences;
    return loadNavPreferences(
      defaultPaths,
      store.settings.nav_preferences as Partial<NavPreferences>
    );
  }, [store, localPreferences, defaultPaths]);

  useEffect(() => {
    // Only the providerless fallback owns this key. Under the provider the
    // account row is the source of truth and PreferencesContext keeps its own
    // paint cache, so writing here would (a) overwrite the very legacy value
    // the one-time migration still needs to lift, and (b) manufacture a
    // default-valued key that makes a fresh browser look like it has
    // preferences worth migrating.
    if (store) return;
    try {
      localStorage.setItem(
        NAV_PREFERENCES_STORAGE_KEY,
        JSON.stringify(preferences)
      );
    } catch {
      // Ignore storage failures; the in-memory ordering still works this session.
    }
  }, [preferences, store]);

  // One setter for both paths: local state drives the providerless fallback,
  // and the namespace write drives the account-scoped one.
  const setPreferences = useCallback(
    (updater: (prev: NavPreferences) => NavPreferences) => {
      const next = updater(preferences);
      setLocalPreferences(next);
      if (store) {
        store.setNamespace('nav_preferences', {
          order: next.order,
          hidden: next.hidden,
        });
      }
    },
    [preferences, store]
  );

  const itemsByPath = useMemo(() => {
    const map = new Map<string, NavItem>();
    defaultItems.forEach((item) => map.set(item.path, item));
    return map;
  }, [defaultItems]);

  const orderedItems = useMemo<OrderedNavItem[]>(() => {
    const hiddenSet = new Set(preferences.hidden);
    return preferences.order
      .map((path) => {
        const item = itemsByPath.get(path);
        if (!item) return null;
        return { ...item, hidden: hiddenSet.has(path) };
      })
      .filter((item): item is OrderedNavItem => item !== null);
  }, [preferences, itemsByPath]);

  const visibleItems = useMemo(
    () => orderedItems.filter((item) => !item.hidden),
    [orderedItems]
  );

  const toggleHidden = useCallback(
    (path: string) => {
      setPreferences((prev) => {
        const hidden = prev.hidden.includes(path)
          ? prev.hidden.filter((entry) => entry !== path)
          : [...prev.hidden, path];
        return { ...prev, hidden };
      });
    },
    [setPreferences]
  );

  const moveBy = useCallback(
    (path: string, delta: number) => {
      setPreferences((prev) => {
        const index = prev.order.indexOf(path);
        if (index === -1) return prev;
        const target = index + delta;
        if (target < 0 || target >= prev.order.length) return prev;
        const order = [...prev.order];
        [order[index], order[target]] = [order[target], order[index]];
        return { ...prev, order };
      });
    },
    [setPreferences]
  );

  const moveUp = useCallback((path: string) => moveBy(path, -1), [moveBy]);
  const moveDown = useCallback((path: string) => moveBy(path, 1), [moveBy]);

  const resetToDefault = useCallback(() => {
    setLocalPreferences(defaultPreferences(defaultPaths));
    if (store) {
      store.resetNamespace('nav_preferences');
    }
  }, [defaultPaths, store]);

  return {
    orderedItems,
    visibleItems,
    toggleHidden,
    moveUp,
    moveDown,
    resetToDefault,
  };
};

export default useNavPreferences;
